import { z } from 'zod';

// ============================================================================
// EMPLOYMENT INFORMATION
// ============================================================================

/**
 * Employment status types for financial profile
 */
export const EmploymentStatusSchema = z.enum([
  'employed',
  'self_employed',
  'retired',
  'unemployed',
  'student',
  'other',
]);

export type EmploymentStatus = z.infer<typeof EmploymentStatusSchema>;

/**
 * Employment information schema
 */
export const EmploymentInfoSchema = z.object({
  employmentStatus: EmploymentStatusSchema,
  // For employed individuals
  employerName: z.string().max(200).optional(),
  jobTitle: z.string().max(100).optional(),
  yearsAtCurrentJob: z.number().min(0).max(100).optional(),
  monthlyIncome: z.number().min(0).optional(),
  // For self-employed individuals
  businessName: z.string().max(200).optional(),
  yearsInBusiness: z.number().min(0).max(100).optional(),
  businessType: z.string().max(100).optional(),
});

export type EmploymentInfo = z.infer<typeof EmploymentInfoSchema>;

// ============================================================================
// ASSETS
// ============================================================================

/**
 * Asset types for financial profile
 */
export const AssetTypeSchema = z.enum([
  'checking',
  'savings',
  'investment',
  'retirement',
  'real_estate',
  'vehicle',
  'other',
]);

export type AssetType = z.infer<typeof AssetTypeSchema>;

/**
 * Asset schema for individual asset entry
 */
export const AssetSchema = z.object({
  id: z.string().uuid(),
  assetType: AssetTypeSchema,
  institution: z.string().min(1).max(200),
  approximateValue: z.number().min(0),
  description: z.string().max(500).optional(),
});

export type Asset = z.infer<typeof AssetSchema>;

// ============================================================================
// LIABILITIES
// ============================================================================

/**
 * Liability types for financial profile
 */
export const LiabilityTypeSchema = z.enum([
  'mortgage',
  'auto_loan',
  'student_loan',
  'credit_card',
  'personal_loan',
  'heloc',
  'other',
]);

export type LiabilityType = z.infer<typeof LiabilityTypeSchema>;

/**
 * Liability schema for individual liability entry
 */
export const LiabilitySchema = z.object({
  id: z.string().uuid(),
  liabilityType: LiabilityTypeSchema,
  creditor: z.string().min(1).max(200),
  monthlyPayment: z.number().min(0),
  balance: z.number().min(0),
  interestRate: z.number().min(0).max(100).optional(),
});

export type Liability = z.infer<typeof LiabilitySchema>;

// ============================================================================
// COMPLETE FINANCIAL PROFILE
// ============================================================================

/**
 * Complete financial profile schema
 * Stored in consumer profile JSON field
 */
export const FinancialProfileSchema = z.object({
  employment: EmploymentInfoSchema.optional(),
  assets: z.array(AssetSchema).default([]),
  liabilities: z.array(LiabilitySchema).default([]),
  lastUpdated: z.string().datetime().optional(),
});

export type FinancialProfile = z.infer<typeof FinancialProfileSchema>;

// ============================================================================
// CONSTANTS & OPTIONS
// ============================================================================

/**
 * Employment status display options
 */
export const EMPLOYMENT_STATUS_OPTIONS = [
  { value: 'employed', label: 'Employed' },
  { value: 'self_employed', label: 'Self-Employed' },
  { value: 'retired', label: 'Retired' },
  { value: 'unemployed', label: 'Unemployed' },
  { value: 'student', label: 'Student' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Asset type display options
 */
export const ASSET_TYPE_OPTIONS = [
  { value: 'checking', label: 'Checking Account' },
  { value: 'savings', label: 'Savings Account' },
  { value: 'investment', label: 'Investment Account' },
  { value: 'retirement', label: 'Retirement Account (401k, IRA)' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'other', label: 'Other Asset' },
] as const;

/**
 * Liability type display options
 */
export const LIABILITY_TYPE_OPTIONS = [
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'auto_loan', label: 'Auto Loan' },
  { value: 'student_loan', label: 'Student Loan' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'personal_loan', label: 'Personal Loan' },
  { value: 'heloc', label: 'HELOC' },
  { value: 'other', label: 'Other Debt' },
] as const;

/**
 * Business type options for self-employed individuals
 */
export const BUSINESS_TYPE_OPTIONS = [
  { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
  { value: 'llc', label: 'LLC' },
  { value: 's_corp', label: 'S Corporation' },
  { value: 'c_corp', label: 'C Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'other', label: 'Other' },
] as const;
